import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createAdminAccount } from 'api/adminAccounts';
import { queryKeys } from 'constants/queryKeys';
import { accountManagementStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TAdminAccount, TCreateAdminAccountRequest } from 'types';
import { toErrorMessage } from './utils';

type UseCreateAdminAccountOptions = {
  onSuccess?: (account: TAdminAccount) => void;
  onError?: (error: unknown) => void;
};

type UseCreateAdminAccountResult = {
  create: (request: TCreateAdminAccountRequest) => void;
  isSubmitting: boolean;
  error: string | undefined;
};

export function useCreateAdminAccount(
  options: UseCreateAdminAccountOptions = {},
): UseCreateAdminAccountResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const strings = accountManagementStrings.create;

  const [error, setError] = useState<string | undefined>();

  const mutation = useMutation({
    mutationFn: async (request: TCreateAdminAccountRequest) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return createAdminAccount(request);
    },
    onSuccess: (account) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_ACCOUNTS, user?.uid],
      });
      setError(undefined);
      options.onSuccess?.(account);
    },
    onError: (caught) => {
      setError(toErrorMessage(caught, strings.errors.submitFailed));
      options.onError?.(caught);
    },
  });

  const create = (request: TCreateAdminAccountRequest) => {
    setError(undefined);
    mutation.mutate(request);
  };

  return {
    create,
    isSubmitting: mutation.isPending,
    error,
  };
}
